// German (de-CH) dashboard copy. This dictionary defines the key set; `en.ts`
// is typed against it. Swiss spelling throughout (never ß), company name
// always lowercase, product names with ® where the brand uses it. Values the
// backend reports verbatim (ids, window labels, URLs) are not in here.

export const de = {
  // --- App shell -----------------------------------------------------------
  "app.title": "Admin Dashboard",
  "app.loading": "Lädt…",
  "header.window": "Fenster:",
  "header.partner": "Partner:",
  "header.partnerTitle": "Alle Panels auf einen Partner einschränken",
  "header.partnerAll": "Alle",
  "header.partnerGroup": "Partner",
  "header.bucketGroup": "Nicht zugeordnet",
  "header.includeTest": "Testsitzungen einbeziehen",
  "header.signOut": "Abmelden",
  "header.language": "Sprache",
  "header.glossary": "Glossar: Begriffe erklärt",
  "header.status": "Dienste: {status} — Details unter Überblick › Zustand",
  "lang.de": "Deutsch",
  "lang.en": "Englisch",
  "window.90d": "3 Mte",
  "window.180d": "6 Mte",
  "window.365d": "Max",
  "staleness.lastRefresh": "Letzte Aktualisierung: {when}",
  "staleness.never": "noch nie",
  "staleness.refresh": "↻ Aktualisieren",
  "relative.seconds": "vor {n} s",
  "relative.minutes": "vor {n} Min",
  "relative.hours": "vor {n} Std",
  "relative.days": "vor {n} Tg",
  "duration.sec": "Sek",
  "duration.min": "Min",
  "duration.hours": "Std",

  // --- Shared words --------------------------------------------------------
  "common.loading": "Lädt…",
  "common.on": "an",
  "common.off": "aus",
  "common.yes": "ja",
  "common.no": "nein",
  "common.count": "Anzahl",
  "common.mode": "Modus",
  "common.model": "Modell",
  "common.turns": "Turns",
  "common.sessions": "Sessions",
  "common.when": "Wann",
  "common.session": "Session",
  "common.type": "Typ",
  "common.metadata": "Metadaten",
  "common.reason": "Grund",
  "common.total": "Total",
  "common.inputTokens": "Input-Tokens",
  "common.outputTokens": "Output-Tokens",
  "common.cost": "Kosten",
  "common.openInInspector": "Im Session Inspector öffnen",
  "common.ratingPositive": "Positiv",
  "common.ratingNegative": "Negativ",

  // --- Sidebar sections and their tabs --------------------------------------
  "nav.sections": "Bereiche",
  "section.overview": "Überblick",
  "section.conversations": "Gespräche",
  "section.usage": "Nutzung",
  "section.quality": "Qualität",
  "section.cost": "Kosten",
  "section.ops": "Betrieb",
  "view.health": "Zustand",
  "view.ops": "Schalter & Budgets",
  "view.customers": "Kunden & Domains",
  "view.testpage": "Testseite",
  "view.kpi": "Erfolg / KPIs",
  "view.pulse": "Live-Puls",
  "view.engagement": "Engagement & Themen",
  "view.peak": "Stosszeiten",
  "view.quality": "Qualität & RAG",
  "view.signals": "Signale & Sicherheit",
  "view.questions": "Fragen-Check",
  "view.talentgeo": "Talent-Geografie",
  "view.cost": "Kosten & Prognosen",
  "view.estimate": "Kosten je Person & Szenarien",
  "view.partner": "Partner-Report",
  "view.aggregates": "Wochen-Aggregate",
  "view.qna": "Letzte Fragen & Antworten",
  "view.sessions": "Session Inspector",
  "view.events": "Ereignis-Stream",
  "view.analyst": "Analyse-Chat",
  "view.insights": "Tagesbericht",
  "view.prompts": "System-Prompts",
  "view.config": "Konfiguration",
  "view.glossary": "Glossar",

  // --- Chat modes / partner buckets (display labels) -----------------------
  "mode.faq": "FAQ",
  "mode.profession": "Beruf",
  "mode.discovery": "Orientierung",
  "mode.trainer": "Skillstrainer",
  "mode.multicheck": "Multicheck",
  "tenant.unknown": "Ohne Origin",
  "tenant.unmapped": "Nicht zugeordnet",
  "tenant.all": "Alle",

  // --- Login ---------------------------------------------------------------
  "login.title": "Admin Dashboard",
  "login.thisAccount": "Dieses Konto",
  "login.forbiddenSuffix": " ist angemeldet, aber für das Dashboard nicht freigegeben.",
  "login.forbiddenHint":
    "Bitte den gateway.one-Administrator um Zugriff auf das Dashboard bitten.",
  "login.signInMsg": "Mit dem gateway.one-Google-Konto anmelden.",
  "login.signInGoogle": "Mit Google anmelden",

  // --- Health & Awareness --------------------------------------------------
  "health.dependencies": "Dienste",
  "health.dependenciesScope":
    "Alle Partner, letzte 60 Minuten — der Partner-Filter gilt für diese Zeile nicht.",
  "health.backendUnreachable":
    "Backend nicht erreichbar — uvicorn auf 127.0.0.1:8080 und VITE_ADMIN_TOKEN prüfen.",
  "health.overall": "Gesamt:",
  "health.version": "Version {v}",
  "health.northStar": "North Star",
  "health.turnsInWindow": "Turns im Fenster",
  "health.engagedSessions": "Vertiefte Sessions",
  "health.engagedSub": "≥3 Turns",
  "health.feedbackRatio": "Feedback-Quote",
  "health.ratings": "{n} Bewertungen",
  "health.anomalies": "Anomalien",
  "health.allClear": "✅ Alles innerhalb der Schwellen in den letzten {window}.",
  "health.dep.latency": "Latenz {ms} ms",
  "health.dep.last": "zuletzt {when}",
  "dep.firestore": "Firestore",
  "dep.geoip": "GeoIP",
  "dep.anthropic_vertex": "Claude (Vertex AI)",
  "dep.gemini_vertex": "Gemini (Vertex AI)",
  "dep.vertex_search": "RAG-Suche (Vertex AI Search)",
  "dep.edge_geo_header": "Geo-Header am Rand (Cloud Armor)",
  "status.ok": "ok",
  "status.degraded": "eingeschränkt",
  "status.down": "ausgefallen",
  "status.unknown": "unbekannt",
  "anomaly.no_answer_rate": "Quote ohne Antwort",
  "anomaly.mid_stream_fail_rate": "Abbrüche im Stream",
  "anomaly.claude_cache_hit_rate": "Cache-Trefferquote (Claude)",
  "anomaly.rag_chunk_sanitized": "RAG-Inhalt bereinigt",
  "anomaly.guardrail_blocked": "Guardrail ausgelöst",
  "anomaly.error_rate": "Fehlerquote",
  "severity.info": "Info",
  "severity.warning": "Warnung",
  "severity.critical": "kritisch",

  // --- Live Pulse ----------------------------------------------------------
  "pulse.active5m": "Aktiv 5 Min",
  "pulse.active1h": "Aktiv 1 Std",
  "pulse.active24h": "Aktiv 24 Std",
  "pulse.turnsSub": "Turns: {n}",
  "pulse.avgQuestions": "Ø {n} Fragen/Session",
  "pulse.modeKickoff": "Einstiegsmodus & Reise",
  "pulse.chart.modeKickoff": "Einstiegsmodus",
  "pulse.topTransitions": "Häufigste Wechsel",
  "pulse.from": "Von",
  "pulse.to": "Nach",
  "pulse.noTransitions": "Keine Modus-Wechsel in diesem Fenster.",
  "pulse.origins": "Herkunft",
  "pulse.embeddingHost": "Einbettende Seite",
  "pulse.noOrigins": "Noch keine Origins erfasst.",
  "pulse.audience": "Zielgruppe",
  "pulse.chart.locale": "Sprache",
  "pulse.chart.device": "Gerät",
  "pulse.chart.country": "Land",

  // --- Success / KPIs ------------------------------------------------------
  "kpi.howToRead": "So lesen wir diese Zahlen",
  "kpi.howToReadText":
    "Gezeigt werden die tatsächlichen Werte aus den serverseitig verfügbaren Daten — keine Soll-Schwellen, kein Ampel-Pass/Fail. Bezugsgrösse ist die Zahl der Sitzungen. Eine Sitzung entsteht, wenn jemand nach der Zustimmung einen Modus wählt oder die erste Nachricht schreibt. Was wir nicht sehen: Wer das Widget nur lädt oder öffnet und wieder geht, erzeugt keine Sitzung — das ist nur in GA4 sichtbar. Mehrere Unterseiten von gateway.one zählen nicht mehrfach: derselbe Browser nutzt am selben Tag dieselbe Sitzung weiter.",
  "kpi.funnel": "Conversion-Funnel",
  "kpi.funnelText":
    "Vom begonnenen bis zum vertieften Gespräch. Prozente jeweils bezogen auf alle Sitzungen{total}.",
  "kpi.privacyWall": "Datenschutz-Wand",
  "kpi.privacyWallText":
    "Eine Sitzung entsteht erst nach der Zustimmung. Wer am Datenschutz-Hinweis abbricht, hinterlässt deshalb keine Sitzung; das zeigt nur GA4. Die Werte hier stammen aus älteren Sitzungen, die noch vor der Zustimmung angelegt wurden, und gehen gegen null.",
  "kpi.shown": "Hinweis gesehen",
  "kpi.shownSub": "Sitzungen mit Erstzustimmungs-Pfad",
  "kpi.accepted": "Akzeptiert",
  "kpi.acceptRate": "Akzeptanzrate: {rate}",
  "kpi.na": "n/v",
  "kpi.stopped": "Abgebrochen",
  "kpi.stoppedSub": "am Datenschutz gestoppt (gesehen, nicht akzeptiert)",
  "kpi.preAccepted":
    "{n} weitere Sitzungen entstanden erst nach der Zustimmung (heute der Normalfall) und zählen hier nicht.",
  "kpi.notMeasurable": "serverseitig nicht messbar",
  "kpi.ofDenominator": "{num} von {den} {label}",
  "kpi.group.Nachfrage": "Nachfrage",
  "kpi.group.Usability": "Usability",
  "kpi.group.Wirkung": "Wirkung",
  "kpi.funnel.opened": "Gespräch begonnen (Sitzung)",
  "kpi.funnel.privacy_accepted": "Datenschutz akzeptiert",
  "kpi.funnel.messaged": "Erste Nachricht gesendet",
  "kpi.funnel.engaged": "Gespräch vertieft (mind. 3 Fragen)",
  "kpi.metric.reichweite.label": "Reichweite",
  "kpi.metric.reichweite.den": "Seitenaufrufe",
  "kpi.metric.reichweite.note":
    "Nicht serverseitig messbar: Wer das Widget nur lädt oder öffnet, aber kein Gespräch beginnt, erzeugt keine Sitzung. Seitenaufrufe und Öffnungen sind nur in GA4 sichtbar (und werden dort über Unterseiten pro Seitenaufruf gezählt). Serverseitig zählen wir begonnene Gespräche.",
  "kpi.metric.wiederkehr.label": "Wiederkehr",
  "kpi.metric.wiederkehr.den": "Sitzungen",
  "kpi.metric.wiederkehr.note":
    "Näherung: Sitzungen, die über mehr als 6 Stunden aktiv waren — derselbe Browser (localStorage) kam nach einer Pause zurück. Kein personenübergreifender Identifier. Untergrenze: Wer den Browser-Speicher löscht und neu kommt, erscheint als neue Sitzung und ist nicht als Wiederkehr erkennbar.",
  "kpi.metric.gespraechstiefe.label": "Gesprächstiefe",
  "kpi.metric.gespraechstiefe.den": "Sitzungen",
  "kpi.metric.gespraechstiefe.note":
    "Anteil der Sitzungen mit mindestens 3 Fragen (gleiche Definition wie North Star und Engagement).",
  "kpi.metric.fruehabbruch.label": "Frühabbruch",
  "kpi.metric.fruehabbruch.den": "Sitzungen",
  "kpi.metric.fruehabbruch.note":
    "Anteil der Sitzungen mit höchstens 1 Frage (Bounce). Ein gewählter Modus ohne Frage zählt dazu; wer schon am Datenschutz-Hinweis oder im Startbildschirm abbricht, erzeugt keine Sitzung und fehlt hier.",
  "kpi.metric.funnel_klick.label": "Funnel-Klick",
  "kpi.metric.funnel_klick.den": "Sitzungen mit Nachricht",
  "kpi.metric.funnel_klick.note":
    "Anteil der Sitzungen mit mindestens einer Frage, in denen eine zitierte Quelle (gateway.one / junior / wiki) angeklickt wurde — der Sprung vom Chat in den Funnel. Quelle: CD18-Beacon.",
  "kpi.metric.hilfreich.label": "Hilfreich-Quote",
  "kpi.metric.hilfreich.den": "abgegebene Bewertungen",
  "kpi.metric.hilfreich.note":
    "Anteil der positiven an allen abgegebenen Daumen-Bewertungen. Freiwillig — nur ein Bruchteil der Nutzenden bewertet, daher kleine Stichprobe.",

  // --- Engagement & Topics -------------------------------------------------
  "engagement.turnVolume": "Turn-Volumen & Zufriedenheit",
  "engagement.chart.turnsPerSession": "Turns pro Session",
  "engagement.feedbackPerMode": "Feedback je Modus",
  "engagement.ratio": "Quote",
  "engagement.noFeedback": "Noch kein Feedback.",
  "engagement.bounceByMode": "Absprungrate je Modus",
  "engagement.bounce": "Absprung",
  "engagement.duration": "Chat-Dauer pro Modus",
  "engagement.chats": "Chats",
  "engagement.multiTurn": "davon ≥2 Turns",
  "engagement.avgDuration": "Ø Dauer",
  "engagement.median": "Median",
  "engagement.max": "Max",
  "engagement.noChats": "Keine Chats im Fenster.",
  "engagement.durationNote":
    "Erste bis letzte Interaktion pro Session und Modus (jeder Tab ist ein eigener Chat). Ø/Median nur über Chats mit ≥2 Turns — Ein-Turn-Chats haben keine messbare Spanne und würden den Schnitt Richtung 0 ziehen.",
  "engagement.discoveryStart": "Orientierung: Einstieg mit Fragen oder freiem Text",
  "engagement.discoveryStart.start": "Einstieg",
  "engagement.discoveryStart.questions": "Schnellstart-Fragen",
  "engagement.discoveryStart.freeText": "Freier Text",
  "engagement.discoveryStart.share": "Anteil",
  "engagement.discoveryStart.turnsPer": "Ø Turns je Gespräch",
  "engagement.discoveryStart.none": "Keine Orientierung-Gespräche im Fenster.",
  "engagement.topics": "Worüber wird gefragt?",
  "engagement.chart.intent": "Absicht der Frage",
  "engagement.chart.sessionIntent": "Worum es in Gesprächen geht",
  "engagement.chart.mentions": "Erwähnte Stichworte",
  "engagement.chart.berufsfeld": "Berufsfelder",
  "engagement.chart.affect": "Stimmung",
  "engagement.replyTurns": "{n} Antworten auf Rückfragen nicht mitgezählt",
  "engagement.replyTurnsOne": "1 Antwort auf eine Rückfrage nicht mitgezählt",
  "engagement.topicsNote":
    "Absicht der Frage, Stichworte und Berufsfelder stammen aus Regex-Klassifikatoren (core/classify.py), rund 30 bis 50 % Trefferquote bei freier Jugendsprache: als Häufung lesen, nicht als Wahrheit pro Turn. «Worum es in Gesprächen geht» bestimmt das Titel-Modell.",
  "engagement.terms": "Gefragte Begriffe",
  "engagement.terms.baseline": "Fenster davor ({window})",
  "engagement.terms.noCompare":
    "Ohne Vergleich: Das doppelte Fenster reicht über den Rückblick hinaus, oder die Abfrage stiess an ihre Grenze.",
  "engagement.terms.none": "Keine Begriffe im Fenster.",
  "engagement.professionTopics": "Beruf-Themen",
  "engagement.chart.professionTopics": "Gespräche je Thema",
  "engagement.professionTopics.conversations": "{n} Beruf-Gespräche im Fenster",
  "engagement.professionTopics.none": "Keine Beruf-Gespräche im Fenster.",
  "professionTopic.alltag": "Alltag",
  "professionTopic.ausbildung": "Ausbildung",
  "professionTopic.anforderungen": "Anforderungen",
  "professionTopic.lohn": "Lohn",
  "professionTopic.arbeitsfelder": "Arbeitsfelder",
  "professionTopic.weiterbildung": "Weiterbildung",
  "professionTopic.spezialisierungen": "Spezialisierungen",
  "professionTopic.sonnenseiten": "Sonnen- und Schattenseiten",
  "professionTopic.zukunft": "Zukunft und KI",
  "engagement.affect": "Stimmungs-Signale",
  "engagement.places": "Genannte Orte (B2B-Analyse)",
  "engagement.places.none": "Keine Orte im Fenster.",
  "engagement.interestVsChoice": "Interesse vs. Wahl",
  "engagement.interestTag": "Interesse",
  "engagement.resolvedBerufsfeld": "Aufgelöstes Berufsfeld",
  "engagement.noGap":
    "Keine Paare aus interest:*-Merkmal und Berufsfeld in diesem Fenster.",
  "engagement.feedbackComments": "Neueste Feedback-Kommentare",
  "engagement.comment": "Kommentar",
  "engagement.noComments": "Noch keine Feedback-Kommentare.",

  // --- The backend's vocabulary (core/classify.py, geo/places.py) ----
  // Intent of a question or a conversation: the topic first, then the shape.
  "intent.multicheck": "Multicheck®",
  "intent.basic_check": "Basic Check®",
  "intent.wayfi": "WayFi®",
  "intent.identyfi": "IdentyFi®",
  "intent.berufskompass": "Berufskompass",
  "intent.assessment": "Eignungstests allgemein",
  "intent.deadline": "Termine & Fristen",
  "intent.schnupperlehre": "Schnupperlehre",
  "intent.lehrstelle": "Lehrstelle finden",
  "intent.interview": "Vorstellungsgespräch",
  "intent.application": "Bewerbung",
  "intent.salary": "Lohn",
  "intent.product_cost": "Preis & Kosten",
  "intent.duration": "Dauer",
  "intent.requirements": "Voraussetzungen",
  "intent.further_education": "Weiterbildung",
  "intent.daily_work": "Berufsalltag",
  "intent.profession_overview": "Berufsbild",
  "intent.location": "Ort & Arbeitsweg",
  "intent.employer": "Lehrbetriebe",
  "intent.privacy": "Datenschutz",
  "intent.platform": "Über gateway.one",
  "intent.meta_bot": "Über den Chatbot",
  "intent.support": "Problem oder Bedienung",
  "intent.comparative": "Vergleich",
  "intent.decisional": "Entscheidungshilfe",
  "intent.procedural": "Wie geht das?",
  "intent.definitional": "Begriffserklärung",
  "intent.constraint": "Einschränkung genannt",
  "intent.setback": "Rückschlag erlebt",
  "intent.rejection": "Widerspruch zum Chatbot",
  "intent.affirmation": "Zustimmung, mehr dazu",
  "intent.smalltalk": "Smalltalk",
  "intent.other": "Sonstiges",
  "affect.positive": "Positiv",
  "affect.frustrated": "Frustriert",
  "affect.confused": "Verwirrt",
  "affect.urgent": "Dringend",
  "affect.uncertain": "Unsicher",
  "affect.profanity": "Kraftausdruck",
  "place.home": "Wohnorte",
  "place.target": "Suchgebiete",
  "place.mention": "Erwähnt",
  "place.col.conversations": "Gespräche",
  "trend.new": "neu",
  "trend.rising": "steigt",
  "trend.falling": "sinkt",
  "terms.col.term": "Begriff",
  "terms.col.conversations": "Gespräche",
  "terms.col.trend": "Trend",

  // --- Peak times ----------------------------------------------------------
  "peak.rightNow": "Jetzt (5 Min)",
  "peak.turns": "Turns",
  "peak.baseline": "Basis 5 Min",
  "peak.baselineSub": "Median für diese Wochenstunde",
  "peak.rateVsBaseline": "Rate vs. Basis",
  "peak.noBaseline": "noch keine Basis",
  "peak.above": "über der Basis",
  "peak.within": "im Rahmen",
  "peak.whenSpike": "Wann ist am meisten los?",
  "peak.chart.heatmap": "Heatmap Stunde × Wochentag",
  "peak.chart.hour": "Tagesstunde",
  "peak.chart.weekday": "Wochentag",
  "peak.localeHour": "Sprache × Stunde",
  "peak.noLocale": "Noch keine Sprachdaten.",
  "peak.tooltipTurns": "{day} {hour} — {n} Turns",
  "weekday.short": "Mo,Di,Mi,Do,Fr,Sa,So",

  // --- Quality & RAG -------------------------------------------------------
  "quality.noAnswerRate": "Quote ohne Antwort",
  "quality.noAnswerSub": "no_answer_found + low_relevance_answer",
  "quality.streamingFailures": "Stream-Abbrüche",
  "quality.streamingSub": "streaming_completed = false",
  "quality.verifier": "Verifier (Multicheck®)",
  "quality.avgConfidence": "Ø Konfidenz: {v}",
  "quality.relevance": "Verteilung der RAG-Relevanz",
  "quality.chart.relevance": "Höchster Relevanz-Score je Antwort",
  "quality.funnel": "Retrieval-Trichter je Modus",
  "quality.col.docsFound": "Dokumente gefunden",
  "quality.col.pagesForwarded": "Seiten weitergegeben",
  "quality.col.pagesCited": "Seiten zitiert",
  "quality.col.fillerDropped": "Füller entfernt (Turns)",
  "quality.col.avgContext": "Ø Kontext-Zeichen",
  "quality.col.snippetOnly": "Nur-Snippet-Turns",
  "quality.noRetrieval": "Keine Retrieval-Turns in diesem Fenster.",
  "quality.topCited": "Meistzitierte Quellen",
  "quality.col.url": "URL",
  "quality.col.title": "Titel",
  "quality.col.sanitizer": "Sanitizer-Treffer",
  "quality.offers": "Lehrstellen — Orte & Berufe (aus zitierten Quellen)",
  "quality.offersNote":
    "Aus den zitierten junior-Lehrstellen abgeleitet (URL + Titel): WO die Stellen sind und WELCHE Berufe gezeigt wurden — nicht, was die Lernenden getippt haben. Distinct nach Session.",
  "quality.topPlaces": "Top Orte (aus zitierten Lehrstellen)",
  "quality.place": "Ort",
  "quality.noPlaces": "Keine Lehrstellen-Orte in diesem Zeitfenster.",
  "quality.topProfessions": "Top Berufe (aus zitierten Lehrstellen)",
  "quality.profession": "Beruf",
  "quality.noProfessions": "Keine Lehrstellen-Berufe in diesem Zeitfenster.",
  "quality.contentGaps": "Inhaltslücken — wiederkehrende Fragen",
  "quality.query": "Frage (normalisiert)",
  "quality.noGaps": "Noch keine wiederkehrenden Fragen.",
  "quality.guardrailReasons": "Guardrail-Gründe (Treffer der Ausgabe-Prüfung)",
  "quality.noGuardrail": "Keine Guardrail-Auslösungen in diesem Fenster.",
  "quality.verifierIssues": "Multicheck® Verifier-Befunde",
  "quality.issue": "Befund",
  "quality.noVerifier": "Keine Verifier-Befunde erfasst.",

  // --- Signals & Safety ----------------------------------------------------
  "signals.safety": "Sicherheit & Missbrauchs-Sonden",
  "signals.turnsInWindow": "Turns im Fenster",
  "signals.offTopic": "Off-Topic-Versuche",
  "signals.attacks": "Prompt-Angriffe",
  "signals.offTopicByMode": "Off-Topic je Modus",
  "signals.attacksByMode": "Prompt-Angriffe je Modus",
  "signals.selfElimination": "Gründe der Selbst-Ausschliessung",
  "signals.selfEliminationSub": "(R7 — das genannte «Nein»)",
  "signals.subflow": "Teil-Signale",
  "signals.multiIntent": "Multi-Intent-Turns",
  "signals.geoRadius": "Lehrstellen-Suchen im Pendelradius",
  "signals.trainerTopics": "Skillstrainer: Interview-Themen",
  "signals.topic": "Thema",
  "signals.mcVariants": "Multicheck®-Varianten",
  "signals.variant": "Variante",
  "signals.mcSubtests": "Multicheck®-Subtests",
  "signals.subtest": "Subtest",
  "signals.zeit": "Multicheck® Zeit-Training",
  "signals.timesBySubtest": "Antwortzeiten pro Subtest",
  "signals.timesByVariant": "Antwortzeiten pro Variante",
  "signals.noTimed": "Keine Zeit-Trainings-Turns im Fenster.",
  "signals.results": "Multicheck® Trainings-Ergebnisse",
  "signals.accBySubtest": "Trefferquote pro Subtest",
  "signals.accByThema": "Trefferquote pro Aufgabenthema",
  "signals.tasks": "Aufgaben",
  "signals.correct": "Richtig",
  // «Richtig» allein las sich als «alle Erfolge»; gezählt wird der ERSTE
  // Versuch, darum steht die Aufschlüsselung daneben.
  "signals.firstTry": "Erster Versuch",
  "signals.afterHelp": "Nach Hinweis",
  "signals.revealed": "Lösung gezeigt",
  "signals.unsolved": "Ungelöst",
  "signals.skippedTasks": "Übersprungen",
  "signals.rate": "Quote",
  "signals.noGraded": "Keine bewerteten Aufgaben im Fenster.",
  "signals.legacyTitle": "Früheres Protokoll (vor Prompt 1.17.0)",
  "signals.legacyRow": "Einzelbewertungen ohne Versuchszählung",
  "signals.record": "Protokoll",
  "signals.coverageBounded":
    "Stichprobe, nicht das ganze Fenster: die Verlaufs-Abfragen sind begrenzt.",
  "signals.coverageSessions": "Sitzungen gelesen",
  "signals.coverageOmitted": "ausgelassen",
  "signals.coverageTruncated": "Verlauf gekürzt",
  "signals.sessionBehaviour": "Session-Verhalten",
  "signals.sessionsInWindow": "Sessions im Fenster",
  "signals.sourceClicks": "Quellen-Klicks",
  "signals.continued": "Weiter nach «keine Antwort»",
  "signals.topClicked": "Meistgeklickte Berufe",
  "signals.modeSwitches": "Modus-Wechsel pro Session",
  "signals.switches": "Wechsel",
  "signals.noneInWindow": "Nichts im Fenster.",
  "signals.n": "N",
  "signals.avg": "Ø",

  // --- Talent geography ----------------------------------------------------
  "talentgeo.minN": "min_n",
  "talentgeo.minNExportable": "10 (k≥10, exportfähig)",
  "talentgeo.overview": "Lehrstellensuche im Fenster",
  "talentgeo.searches": "Suchen (Count-Gate)",
  "talentgeo.withHome": "mit Wohn-Gemeinde",
  "talentgeo.commuteAvail": "ÖV-Zeiten verfügbar",
  "talentgeo.zero": "Null-Treffer-Suchen",
  "talentgeo.suppressed": "unterdrückte Gemeinden (min_n)",
  "talentgeo.demand": "Nachfrage nach Wohn-Gemeinde",
  "talentgeo.offers": "Gezeigte Angebote nach Gemeinde",
  "talentgeo.cantons": "Kantone & Top-Berufe",
  "talentgeo.demandByCanton": "Nachfrage nach Kanton",
  "talentgeo.canton": "Kanton",
  "talentgeo.topProfessions": "Top-Berufe (Suchbegriff)",
  "talentgeo.col.commune": "Gemeinde",
  "talentgeo.col.ct": "Kt.",
  "talentgeo.col.searches": "Suchen",
  "talentgeo.col.zero": "0 Treffer",
  "talentgeo.col.medianNearest": "Median nächstes Angebot",
  "talentgeo.col.noOffer30": "kein Angebot ≤30'",
  "talentgeo.col.noOffer60": "kein Angebot ≤60'",
  "talentgeo.col.topProfessions": "Top-Berufe",
  "talentgeo.col.impressions": "Impressionen",
  "talentgeo.col.medianDistance": "Median-Distanz zur Nachfrage",
  "talentgeo.noDemand":
    "Keine Lehrstellensuche mit Wohn-Gemeinde im Fenster (oder alle Zeilen unter min_n).",
  "talentgeo.noOffers": "Keine gezeigten Angebote mit Gemeinde-Zuordnung im Fenster.",
  "talentgeo.minutes": "{n} Min.",
  "talentgeo.bfs": "BFS {n}",

  // --- Cost & Estimates ----------------------------------------------------
  "cost.todaySoFar": "Heute bisher",
  "cost.projectedEod": "Prognose Tagesende: {v}",
  "cost.last7d": "Letzte 7 Tage",
  "cost.monthlyProjection": "Monatsprognose: {v}",
  "cost.annualized": "Aufs Jahr",
  "cost.annualizedSub": "letzte 30 Tage × 12,17",
  "cost.incomplete":
    "Lesegrenze erreicht: Aus diesem Fenster sind nur die neuesten {n} Turns gezählt, mehr liest eine Abfrage nicht. Summen, Tagesverlauf und Tabellen fallen zu tief aus. Für vollständige Zahlen ein kürzeres Fenster wählen.",
  "cost.spanIncomplete":
    "Lesegrenze erreicht: Aus den letzten {days} Tagen sind nur die neuesten {n} Turns gezählt. Die Zahlen dieser Karte fallen zu tief aus; die Fensterwahl ändert daran nichts.",
  "cost.efficiency": "Effizienz",
  "cost.perTurn": "Kosten / Turn",
  "cost.perTurnSub": "{turns} Turns · {perSession} pro Session ({sessions})",
  "cost.cacheRw": "Cache gelesen / geschrieben",
  "cost.cacheRwSub": "{read} gelesen / {write} geschrieben · Break-even bei {breakEven}",
  "cost.windowTotal": "Fenster gesamt",
  "cost.windowTotalSub": "Summe über alle Modi",
  "cost.cacheSavings": "Cache-Ersparnis (Claude-Prompt-Cache)",
  "cost.cacheReads": "Cache-Lesungen",
  "cost.cacheReadsSub": "Token aus dem Cache bedient",
  "cost.savedOnReads": "Gespart beim Lesen",
  "cost.savedOnReadsSub":
    "vs. voller Eingabe-Tarif ({naive}); Aufpreis fürs Schreiben nicht abgezogen",
  "cost.netSaving": "Netto-Ersparnis",
  "cost.netSavingSub": "nach dem Schreib-Aufpreis von {premium}; Lesen kostete {actual}",
  "cost.chart.daily": "Täglich",
  "cost.chart.area": "Verlauf",
  "cost.chart.cumulative": "Kumuliert",
  "cost.chart.cumulativeTitle": "Kumulierte Kosten im Fenster (USD ≈ CHF)",
  "cost.chart.dailyTitle": "Kosten pro Tag (USD ≈ CHF)",
  "cost.series.input": "Eingabe",
  "cost.series.output": "Ausgabe",
  "cost.series.cache_read": "Cache gelesen",
  "cost.series.cache_create": "Cache geschrieben",
  "cost.series.cumulative": "kumuliert",
  "cost.perTenant": "Kosten je Partner und Modus",
  "cost.perTenantModeHint":
    "Fett das Partner-Total, darunter die Modi mit dem Modell, das sie bepreist hat — die Zeile, auf der eine Rechnungsposition steht.",
  "cost.col.partner": "Partner",
  "cost.col.modeModel": "Modus / Modell",
  "cost.col.turns": "Turns",
  "cost.col.sessions": "Sessions",
  "cost.col.inputTok": "Input-Token",
  "cost.col.outputTok": "Output-Token",
  "cost.col.cacheRead": "Cache gelesen",
  "cost.col.cacheCreate": "Cache geschrieben",
  "cost.col.cost": "Kosten",
  "cost.col.perTurn": "Ø / Turn",
  "cost.col.perSession": "Ø / Session",
  "cost.col.avgTokSession": "Ø Token/Session",
  "cost.ratePer1m": "{rate} $/1M Eingabe",
  "cost.exportCsv": "⬇ CSV für Abrechnung",
  "cost.exportCsvTitle": "Partner × Modus als CSV (Excel) herunterladen",
  "cost.unattributed":
    "{n} Turns stammen aus Sessions, die vor dem Fenster begonnen haben — sie zählen unter «Ohne Origin» und nicht zu einem Partner.",
  "cost.perMode": "Aufschlüsselung je Modus",
  "cost.export.meta.window": "Fenster",
  "cost.export.meta.filter": "Partner-Filter",
  "cost.export.meta.generated": "Erzeugt",
  "cost.export.meta.basis": "Grundlage",
  "cost.export.meta.basisNote":
    "LLM-Aufwand aus gespeicherten Token-Zahlen × Preistabelle (USD); Titel-, Recap- und Suchplanungs-Aufrufe sowie Infrastruktur nicht enthalten — keine Rechnung.",
  "cost.export.total": "Total",
  "cost.export.col.partner": "Partner",
  "cost.export.col.mode": "Modus",
  "cost.export.col.model": "Modell",
  "cost.export.col.turns": "Turns",
  "cost.export.col.sessions": "Sessions",
  "cost.export.col.inputTokens": "Input-Tokens",
  "cost.export.col.outputTokens": "Output-Tokens",
  "cost.export.col.cacheRead": "Cache gelesen",
  "cost.export.col.cacheCreate": "Cache geschrieben",
  "cost.export.col.costUsd": "Kosten USD",
  "cost.export.col.perTurn": "USD/Turn",
  "cost.export.col.perSession": "USD/Session",

  // --- Cost per person & scenarios ------------------------------------------
  "estimate.perPerson": "Kosten je Person (letzte 30 Tage)",
  "estimate.segment.all": "Alle",
  "estimate.segment.anonymous": "Anonym (je Session)",
  "estimate.segment.signed_in": "Angemeldet (je Pseudonym)",
  "estimate.col.segment": "Gruppe",
  "estimate.col.people": "Personen",
  "estimate.col.sessionsPerPerson": "Ø Sessions / Person",
  "estimate.col.turnsPerPerson": "Ø Turns / Person",
  "estimate.col.mean": "Ø Kosten",
  "estimate.col.p25": "P25",
  "estimate.col.median": "Median",
  "estimate.col.p90": "P90",
  "estimate.minPeople": "Perzentile und Szenarien ab {n} Personen je Gruppe.",
  "estimate.profiles": "Gemessenes Profil je Modus",
  "estimate.col.inputPerTurn": "Ø Input / Turn",
  "estimate.col.outputPerTurn": "Ø Output / Turn",
  "estimate.col.cacheReadPerTurn": "Ø Cache gelesen / Turn",
  "estimate.col.cacheWritePerTurn": "Ø Cache geschrieben / Turn",
  "estimate.col.usdPerTurn": "Ø Kosten / Turn",
  "estimate.scenarios": "Szenario-Rechner",
  "estimate.peoplePerMonth": "Personen pro Monat",
  "estimate.apply": "Berechnen",
  "estimate.scenario.defensive": "Defensiv",
  "estimate.scenario.normal": "Normal",
  "estimate.scenario.offensive": "Offensiv",
  "estimate.basis.p25": "wie das untere Viertel (P25)",
  "estimate.basis.mean": "wie der Durchschnitt",
  "estimate.basis.p90": "wie die intensivsten 10 % (P90)",
  "estimate.perMonthSub": "pro Monat · {v} je Person, {basis}",
  "estimate.tooFewPeople":
    "Zu wenige Personen in den letzten 30 Tagen für Szenarien (mindestens {n}).",
  "estimate.normalByMode": "Normal-Szenario je Modus",
  "estimate.col.perPerson": "je Person",
  "estimate.col.perMonth": "pro Monat",
  "estimate.basisNote":
    "Modellkosten (LLM) nach der Preistabelle, USD ≈ CHF; ohne Titel, Recaps, Suchplanung, Suche und Infrastruktur.",
  "estimate.incomplete":
    "Lesegrenze erreicht: Aus den letzten 30 Tagen sind nur die neuesten {n} Turns gezählt, mehr liest eine Abfrage nicht. Wer nur früher chattete, fehlt in den Personenzahlen, und angemeldete Personen zählen nur mit ihren jüngsten Sessions, ihre Kosten fallen zu tief aus. Die Werte je anonyme Person bleiben aussagekräftig: sie beruhen auf den jüngsten Tagen.",
  "estimate.limits": "Wer ans Limit stösst (letzte 30 Tage)",
  "estimate.col.limitHit": "Am Limit",
  "estimate.col.limitShare": "Anteil",
  "estimate.limitReason.questions": "Fragenlimit",
  "estimate.limitReason.cost": "Kostenlimit anonym",
  "estimate.limitReason.daily_limit": "Tageslimit angemeldet",
  "estimate.limitWarning": "über {share}",
  "estimate.limitNote":
    "Bis {share} einer Gruppe am Limit ist unauffällig. Darüber stossen viele an: dann lohnt es sich, die Limits unter Betrieb › Schalter & Budgets zu prüfen. Die Warnung gilt ab {n} Personen je Gruppe.",
  "estimate.signedInAfterLimit":
    "{n} Personen stiessen ohne Login ans Limit und meldeten sich danach an.",
  "estimate.caps": "Limits berücksichtigen",
  "estimate.capOff": "unbegrenzt",
  "estimate.capsInForce":
    "Mit den Limits in Kraft: anonym {anonymous} pro Session, angemeldet {signedIn} pro Person und Tag, alle zusammen {daily} pro Tag.",
  "estimate.capsMonth": "Ein Monat kostet damit höchstens {month}.",
  "estimate.budgetBound": "Vom Tagesbudget begrenzt: an vollen Tagen pausiert der Chat.",

  // --- Who chatted (Stosszeiten) --------------------------------------------
  "activity.title": "Wer hat gechattet?",
  "activity.today": "Heute bisher",
  "activity.week": "Letzte 7 Tage",
  "activity.month": "Letzte 30 Tage",
  "activity.perDay": "Personen pro Tag im Schnitt",
  "activity.change": "{change} gegenüber den {days} Tagen davor (Ø {previous})",
  "activity.noPrevious": "Kein Vergleich: in den {days} Tagen davor keine Gespräche",
  "activity.people": "Personen im Zeitraum: {n}",
  "activity.split": "{signedIn} angemeldet, {anonymous} anonym",
  "activity.chart": "Personen pro Tag, letzte 30 Tage",
  "activity.signedIn": "Angemeldet",
  "activity.anonymous": "Anonym",
  "activity.scope":
    "Volle Tage bis gestern (Zürich), unabhängig vom Zeitfenster oben; der Partner-Filter gilt. Gezählt wird, wer mindestens eine Frage gestellt hat.",
  "activity.incomplete":
    "Mehr Sessions als die Lesegrenze: die ältesten Tage sind unvollständig, der Vergleich mit dem Vormonat fällt zu tief aus.",

  // --- Partner report ------------------------------------------------------
  "partner.select": "— wählen —",
  "partner.partner": "Partner",
  "partner.sessionsSuffix": "{n} Sessions",
  "partner.noMapping":
    "Noch keine Partner-Zuordnung konfiguriert (CHATBOT_ORIGIN_TENANT_MAP) — es stehen nur die beiden eingebauten Sammel-Kategorien zur Verfügung.",
  "partner.minNWarn":
    "min_n = {n}: nur für die interne Ansicht. Was an einen Partner geht, muss mit min_n ≥ {k} erzeugt werden.",
  "partner.pickAbove": "Partner oben auswählen.",
  "partner.reach": "Reichweite",
  "partner.engagedSub": "{n} mit ≥ 3 Fragen",
  "partner.avgTurns": "Ø {n} pro Session",
  "partner.satisfaction": "Zufriedenheit",
  "partner.cost": "Kosten",
  "partner.totalInWindow": "Total im Fenster",
  "partner.perTurn": "Pro Turn",
  "partner.perSession": "Pro Session",
  "partner.noTurns": "Keine Turns im Fenster.",
  "partner.quality": "Qualität",
  "partner.noGoodAnswer": "Ohne gute Antwort",
  "partner.turnsN": "{n} Turns",
  "partner.abortedStreams": "Abgebrochene Streams",
  "partner.abortedSub": "technische Fehler mitten in der Antwort",
  "partner.ratedAnswers": "Bewertete Antworten",
  "partner.ratedSub": "Basis der Zufriedenheitsquote",
  "partner.content": "Inhalts-Profil",
  "partner.language": "Sprache",
  "partner.intent": "Absicht der Frage",
  "partner.berufsfeld": "Berufsfeld",
  "partner.suppressed":
    "{n} Zeilen unterdrückt (unter min_n = {k}) — zu wenig Verkehr, um sie anonym auszuweisen. Nicht «kein Interesse».",
  "partner.limits": "Grundlage & Grenzen",
  "partner.mapping": "Zuordnung",
  "partner.builtinBucket": "Eingebaute Sammel-Kategorie, kein konfigurierter Partner.",
  "partner.hostsSeen": "Beobachtete Hosts",
  "partner.unattributedRow": "Nicht zugeordnete Turns",
  "partner.unattributedText":
    "{n} — aus Sessions, die vor dem Fenster begonnen haben. Sie fehlen hier, statt einem falschen Partner zugeschlagen zu werden.",
  "partner.notIncluded": "Nicht enthalten",
  "partner.notIncludedText":
    "Titel-, Recap- und Suchplanungs-Aufrufe (sie erzeugen keinen Turn) sowie sämtliche Infrastrukturkosten (Networking, Cloud Run, Firestore) — diese lassen sich nicht pro Partner zuordnen. Die Zahlen sind LLM-Aufwand, keine Rechnungsgrundlage.",

  // --- Weekly aggregates ---------------------------------------------------
  "aggregates.title": "Wochen-Aggregate",
  "aggregates.noneTitle": "Noch keine Wochen-Aggregate",
  "aggregates.noneText":
    "Der wöchentliche Aggregations-Job hat noch kein Aggregat für eine abgeschlossene ISO-Woche geschrieben. Sobald er läuft, erscheinen hier Nachfrage, Inhaltslücken und Berufsfeld-Wechsel. Jede Zelle ist k≥10-unterdrückt — Wochen mit wenig Verkehr bleiben darum absichtlich leer.",
  "aggregates.demand": "Nachfrage nach Berufsfeld",
  "aggregates.demandMeta": "{turns} Turns im Fenster · {suppressed} Zelle(n) unterdrückt unter k={k}",
  "aggregates.berufsfeld": "Berufsfeld",
  "aggregates.locale": "Sprache",
  "aggregates.turns": "Turns",
  "aggregates.noCell": "Keine Zelle hat diese Woche k={k} erreicht.",
  "aggregates.unknown": "(unbekannt)",
  "aggregates.contentGaps": "Inhaltslücken",
  "aggregates.gapsMeta": "Relevanz-Boden {floor} · {suppressed} Zelle(n) unterdrückt unter k={k}",
  "aggregates.noAnswer": "Keine Antwort",
  "aggregates.lowRelevance": "Geringe Relevanz",
  "aggregates.noGapCell": "Keine Inhaltslücken-Zelle hat diese Woche k={k} erreicht.",
  "aggregates.pivot": "Berufsfeld-Wechsel",
  "aggregates.pivotMeta":
    "{sessions} Session(s) mit mehreren Feldern · {suppressed} Zelle(n) unterdrückt unter k={k}",
  "aggregates.from": "Von",
  "aggregates.to": "Nach",
  "aggregates.noPivot": "Kein Wechsel hat diese Woche k={k} erreicht.",

  // --- Recent Q&A ----------------------------------------------------------
  "qna.title": "Letzte Fragen & Antworten ({n})",
  "qna.col.when": "Wann",
  "qna.col.mode": "Modus",
  "qna.col.question": "Frage",
  "qna.col.answer": "Antwort",
  "qna.col.sources": "Quellen",
  "qna.col.session": "Session",
  "qna.none": "Keine Fragen in diesem Zeitfenster.",

  // --- Session inspector ---------------------------------------------------
  "sessions.recent": "Letzte Sessions",
  "sessions.col.when": "Wann",
  "sessions.col.mode": "Modus",
  "sessions.col.q": "F",
  "sessions.col.tags": "Tags",
  "sessions.noneRecent": "Keine Sessions im Abfragefenster.",
  "sessions.lookup": "Session per ID suchen",
  "sessions.placeholder": "session_id einfügen",
  "sessions.reload": "Neu laden",
  "sessions.metadata": "Session-Metadaten",
  "sessions.export": "⬇ Export Excel/CSV",
  "sessions.exportTitle":
    "Ganze Konversation (alle Turns + Metadaten) als CSV für Excel herunterladen",
  "sessions.turns": "Turns ({n})",
  "sessions.widgetView": "Widget-Ansicht",
  "sessions.widgetViewTitle":
    "Antworten wie im Widget rendern (Markdown + SVG), zum Prüfen von Grafiken und Format",
  "sessions.user": "Nutzer/in",
  "sessions.assistant": "Assistent",
  "sessions.guardrail": "Guardrail: {reason}",
  "sessions.guardrailYes": "ja",
  "sessions.midStreamFail": "Stream abgebrochen",
  "sessions.tokens": "Tokens: {in} ein / {out} aus",
  "sessions.cacheHit": " · Cache-Treffer",
  "sessions.intent": "Absicht: {v}",
  "sessions.berufsfeld": " · Berufsfeld: {v}",
  "sessions.retrievalTitle": "gefunden → weitergegeben → zitiert",
  "sessions.retrievalLegend":
    "Pro Turn: «Abrufthema» ist der Beruf, auf den die Suche verankert war, «Suchanfrage» der bereinigte Text, der samt Katalog-Labels an Vertex ging, «latency» die Zeit bis zum ersten Token und bis zum Ende der Antwort. Zusammen zeigen sie, warum ein Turn zitiert hat, was er zitiert hat, und was eine Denktiefe kostet.",
  "sessions.retrievalTopic": "Abrufthema: {v}",
  "sessions.searchQuery": "Suchanfrage (Vertex): «{v}»",
  "sessions.retrievalContextTitle":
    "Woran die Suche dieses Turns verankert war und was an Vertex ging",
  "sessions.latencyTitle":
    "Zeit bis zum ersten Token und bis zum Ende der Antwort, serverseitig gemessen",
  "sessions.depthTitle":
    "Denktiefe, mit der das Modell diese Antwort geschrieben hat: Effort bei Claude, Thinking bei Gemini. Sie erklärt Antwortzeit und Kosten dieses Turns.",
  "sessions.sources": "Quellen ({n}):",
  "sessions.events": "Ereignisse ({n})",
  "sessions.selectHint": "Session in der Seitenleiste wählen oder eine session_id einfügen.",
  "sessions.meta.id": "Session-ID",
  "sessions.meta.created": "Erstellt",
  "sessions.meta.updated": "Aktualisiert",
  "sessions.meta.duration": "Dauer",
  "sessions.meta.locale": "Sprache",
  "sessions.meta.country": "Land",
  "sessions.meta.device": "Gerät",
  "sessions.meta.mode": "Einstiegsmodus",
  "sessions.meta.startedModes": "Modi-Verlauf",
  "sessions.meta.turns": "Turns",
  "sessions.meta.questions": "Fragen",
  "sessions.meta.isTest": "Testsitzung",
  "sessions.meta.title": "Titel",
  "sessions.tags.positive": "{n} positive Rückmeldungen",
  "sessions.tags.negative": "{n} negative Rückmeldungen",
  "sessions.tags.flagged": "{n} Turn markiert",
  "sessions.tags.flaggedPlural": "{n} Turns markiert",
  "sessions.meta.userHash": "Benutzer-Hash",
  "sessions.meta.userHashLookup": "Alle Sessions dieses Logins",
  "sessions.erasure.lookupTitle": "Sessions eines Logins",
  "sessions.erasure.lookupHelp":
    "Für ein Lösch- oder Auskunftsgesuch einer angemeldeten Person: ihr Name oder ihr Benutzer-Hash. Der Benutzer-Hash ist ein Einweg-Hash des Namens; der Name wird nur gehasht und nirgends gespeichert. Gefunden werden nur Sessions mit Login; eine anonyme Session lässt sich keiner Person zuordnen. Gleichnamige Personen haben denselben Benutzer-Hash, die Liste kann also Sessions anderer Personen enthalten. Bei einer Auskunft darum nur Sessions herausgeben, die die Person selbst mit Datum und Inhalt bezeichnet; kann sie das nicht, ist nur eine Löschung möglich.",
  "sessions.erasure.lookupLabel": "Name oder Benutzer-Hash",
  "sessions.erasure.lookupPlaceholder": "Vorname Nachname oder 64-stelliger Hash",
  "sessions.erasure.lookupRun": "Sessions suchen",
  "sessions.erasure.none": "Keine Session mit diesem Login.",
  "sessions.erasure.hash": "Benutzer-Hash: {hash}",
  "sessions.erasure.col.role": "Rolle",
  "sessions.erasure.col.preview": "Erste Frage",
  "sessions.erasure.deleteTitle": "Session löschen",
  "sessions.erasure.deleteHelp":
    "Löscht diese eine Session mit allen Turns und Ereignissen endgültig. Ein Löschprotokoll (wer, wann, Grund, Anzahl) bleibt 365 Tage. Weitere Sessions desselben Logins bleiben bestehen und werden je einzeln gelöscht.",
  "sessions.erasure.reason": "Grund, z. B. Löschgesuch per E-Mail vom 20.10.2026",
  "sessions.erasure.confirm": "Zur Bestätigung «{text}» eintippen",
  "sessions.erasure.delete": "Session endgültig löschen",
  "sessions.erasure.done": "Session {id}… gelöscht: {turns} Turns, {events} Ereignisse.",
  "sessions.erasure.nothing": "Unter {id}… war nichts gespeichert.",
  "sessions.erasure.goneHint":
    "Die Session ist nicht mehr gespeichert (gelöscht oder abgelaufen). Eine Löschung, die nach dem Dokument abgebrochen ist, lässt sich hier wiederholen: sie entfernt, was von den Turns und Ereignissen noch da ist.",
  "sessions.erasure.failed_document":
    "Löschen fehlgeschlagen: die Session konnte nicht gelöscht werden, nichts wurde entfernt. Bitte erneut löschen.",
  "sessions.erasure.failed_purge":
    "Löschen unvollständig: die Session ist gelöscht, {turns} Turns und {events} Ereignisse entfernt; der Rest fehlt noch. Bitte erneut löschen.",

  // --- CSV export of one session -------------------------------------------
  "export.col.nr": "Nr",
  "export.col.time": "Zeit",
  "export.col.mode": "Modus",
  "export.col.model": "Modell",
  "export.col.provider": "Anbieter",
  "export.col.depth": "Denktiefe",
  "export.col.question": "Frage",
  "export.col.answer": "Antwort",
  "export.col.sources": "Quellen",
  "export.col.feedback": "Feedback",
  "export.col.feedbackComment": "Feedback-Kommentar",
  "export.col.intent": "Intent",
  "export.col.berufsfeld": "Berufsfeld",
  "export.col.mentions": "Erwähnungen",
  "export.col.affect": "Stimmung",
  "export.col.traits": "Merkmale",
  "export.col.inputTokens": "Input-Tokens",
  "export.col.outputTokens": "Output-Tokens",
  "export.col.cacheHit": "Cache-Hit",
  "export.col.guardrail": "Guardrail",
  "export.col.streamOk": "Stream OK",
  "export.col.hits": "Treffer (abgerufen/genutzt)",
  "export.col.maxRelevance": "Max. Relevanz",
  "export.col.docsFound": "Dokumente gefunden",
  "export.col.pagesForwarded": "Seiten weitergegeben",
  "export.col.sourcesCited": "Quellen zitiert",
  "export.col.belowFloor": "Unter Relevanz-Boden",
  "export.col.contextChars": "Kontext-Zeichen",
  "export.col.retrievalTopic": "Abrufthema",
  "export.col.searchQuery": "Suchanfrage (Vertex)",
  "export.col.ttft": "Zeit bis erstes Token (ms)",
  "export.col.latency": "Antwortdauer (ms)",
  "export.col.turnId": "Turn-ID",

  // --- Events stream + metadata summaries ----------------------------------
  "events.filter": "Filter:",
  "events.allTypes": "(alle Typen)",
  "events.title": "Letzte Analyse-Ereignisse ({n})",
  "events.none": "Keine Ereignisse für diesen Filter.",
  "eventmeta.source": "Quelle",
  "eventmeta.profession": "Beruf",
  "eventmeta.hits": "{n} Treffer · max Score {score}",
  "eventmeta.offersWithCommute": "{n} Lehrstellen mit ÖV-Zeit",
  "eventmeta.sourceSanitized": "Quelle bereinigt",
  "eventmeta.withReason": "mit Begründung",
  "eventmeta.withoutReason": "ohne Begründung",
  "eventmeta.turn": "Turn",

  // --- System prompts ------------------------------------------------------
  "prompts.version": "Prompt-Version",
  "prompts.disabled": "deaktiviert",
  "prompts.model": "Modell",
  "prompts.chars": "{n} Zeichen",
  "prompts.preamble": "Präambel",
  "prompts.modelDefault": "Modell-Standard",
  "prompts.adaptive": "adaptiv",
  "prompts.thinking": "Thinking",
  "prompts.effort": "Effort",
  "prompts.copy": "Prompt kopieren",
  "prompts.copied": "Kopiert.",
  "prompts.copyFailed": "Kopieren ging nicht: den Text markieren und von Hand kopieren.",

  // --- Config --------------------------------------------------------------
  "config.edge": "Schutz am Rand (Cloud-Armor-Geo-Block)",
  "config.edgeNotReported": "Das Backend meldet die Edge-Policy noch nicht.",
  "config.edgeNotConfigured":
    "Keine Cloud-Armor-Policy an diese Instanz übergeben (lokale Entwicklung). In Prod hängt Terraform den DACH-Geo-Block an den Load Balancer.",
  "config.row.policy": "Sicherheits-Policy",
  "config.row.allowedEdge": "Erlaubte Länder (Rand)",
  "config.row.everythingElse": "Alles andere",
  "config.blockedAtEdge": "am Rand blockiert (HTTP 403), erreicht die App nie",
  "config.row.rateLimit": "Rate-Limit pro IP",
  "config.rateLimitOn": "an · {n} Anfragen/Min ohne Widget-Dateien, danach 10 Min Sperre",
  "config.row.botProbe": "Bot-Sonden blockieren",
  "config.botProbeOn": "an · Scanner-Pfade + UAs abgewiesen",
  "config.row.blockedSee": "Blockierte Besucher sehen",
  "config.blockedSeeNotice":
    "den Regions-Hinweis des Widgets (statische Assets weltweit, API weiterhin blockiert)",
  "config.blockedSeeNothing": "nichts (auch Widget-Assets blockiert, keine Sprechblase)",
  "config.row.appBackstop": "App-Backstop (allowed_countries)",
  "config.row.listsMatch": "Rand- und App-Liste stimmen überein",
  "config.modes": "Modell je Modus",
  "config.col.mode": "Modus",
  "config.col.model": "Modell",
  "config.col.provider": "Anbieter",
  "config.col.depth": "Denktiefe",
  "config.col.enabled": "Aktiv",
  "config.flags": "Feature-Flags",
  "config.flag.adminEnabled": "Admin aktiviert",
  "config.flag.adminToken": "Admin-Token gesetzt",
  "config.flag.multicheck": "Multicheck® aktiviert",
  "config.flag.modelArmor": "Model Armor aktiviert",
  "config.flag.reranker": "Reranker aktiviert",
  "config.flag.tracing": "Tracing aktiviert",
  "config.flag.tracingRate": "Tracing-Stichprobe",
  "config.flag.metrics": "Metriken aktiviert",
  "config.flag.testKeyword": "Test-Session-Schlüsselwort gesetzt",
  "config.limits": "Ressourcen-Pins & Limits",
  "config.limit.project": "GCP-Projekt",
  "config.limit.region": "GCP-Region",
  "config.limit.firestore": "Firestore-Datenbank",
  "config.limit.promptVersion": "Prompt-Version",
  "config.limit.allowedCountries": "Erlaubte Länder",
  "config.limit.anonLimit": "Fragen-Limit anonym",
  "config.limit.rateSession": "Rate-Limit / Session",
  "config.limit.rateIp": "Rate-Limit / IP",
  "config.limit.historyTurns": "History max. Turns",
  "config.limit.historyTokens": "History Soft-Max Tokens",
  "config.limit.ttl": "TTL anonym (Tage)",
  "config.knobs": "Dashboard-Einstellungen",
  "config.knob.recent": "Limit letzte Sessions",
  "config.knob.maxWindow": "Max. Fenster (Tage)",

  // --- Betrieb (runtime switches, budgets, reset) -------------------------
  "ops.reason": "Grund der Änderung",
  "ops.reason.placeholder": "Warum wird geändert? Wird mit jeder Änderung protokolliert.",
  "ops.reason.missing": "Ohne Grund (mindestens 10 Zeichen) lässt sich nichts speichern.",
  "ops.saving":
    "Speichert… Ein Modell- oder Denktiefe-Wechsel wird zuerst beim Anbieter geprüft, das dauert bis zu einer Minute.",
  "ops.saved": "Gespeichert (Version {version}). Wirkt auf allen Seiten innert etwa einer Minute.",
  "ops.lastChange": "Letzte Änderung {when} von {who}",
  "ops.chat": "Chat an / aus",
  "ops.chat.on": "Der Chat ist an",
  "ops.chat.paused": "Der Chat ist pausiert",
  "ops.chat.pausedBySwitch":
    "Von Hand pausiert: kein Chat-Knopf auf den Seiten, neue Fragen werden ohne Modellaufruf abgelehnt.",
  "ops.chat.pausedByBudget":
    "Das Tagesbudget ist aufgebraucht: Der Chat pausiert bis Mitternacht (Zürich) oder bis das Tagesbudget erhöht wird.",
  "ops.chat.pause": "Chat pausieren",
  "ops.chat.resume": "Chat wieder einschalten",
  "ops.chat.raiseBudget": "Tagesbudget ändern",
  "ops.chat.pausePermanently": "Dauerhaft pausieren",
  "ops.chat.pausePermanentlyHint":
    "«Dauerhaft pausieren» hält den Chat auch über Mitternacht oder die Ruhezeit hinaus an, etwa für eine Wartung, bis ihn jemand wieder einschaltet.",
  "ops.chat.pausedBySchedule":
    "Ruhezeit {start}–{end} Uhr (Zürich): Der Chat pausiert bis {end} Uhr und läuft dann von selbst weiter.",
  "ops.chat.changeQuietHours": "Ruhezeit ändern",
  "ops.quiet": "Ruhezeit",
  "ops.quiet.current": "Jeden Tag von {start} bis {end} Uhr (Zürich) pausiert.",
  "ops.quiet.none": "Keine Ruhezeit: Der Chat läuft rund um die Uhr.",
  "ops.quiet.from": "Von",
  "ops.quiet.to": "Bis",
  "ops.quiet.save": "Ruhezeit speichern",
  "ops.quiet.remove": "Ruhezeit entfernen",
  "ops.chat.budgetAlsoSpent":
    "Auch das Tagesbudget ist aufgebraucht: Wieder eingeschaltet bleibt der Chat pausiert bis Mitternacht (Zürich) oder bis das Tagesbudget erhöht wird.",
  "ops.notice.close": "Meldung schliessen",
  "ops.spend": "Modellkosten heute",
  "ops.spend.ofBudget": "von {budget} Tagesbudget ({percent})",
  "ops.spend.noBudget": "Kein Tagesbudget gesetzt",
  "ops.spend.turns": "{n} Turns",
  "ops.spend.warn": "Über 80 % des Tagesbudgets verbraucht.",
  "ops.spend.reached": "Tagesbudget erreicht: Der Chat ist pausiert.",
  "ops.spend.days": "Letzte 30 Tage: {total}",
  "ops.budgets": "Budgets",
  "ops.budget.anonymous": "Anonym, je Session",
  "ops.budget.signedIn": "Angemeldet, je Person und Tag",
  "ops.budget.daily": "Alle zusammen, je Tag",
  "ops.budget.col.budget": "Budget",
  "ops.budget.col.baseline": "Terraform",
  "ops.budget.col.effective": "In Kraft",
  "ops.budget.col.new": "Neuer Wert",
  "ops.budget.placeholder": "leer = Terraform",
  "ops.budget.off": "aus",
  "ops.budget.invalid": "Nur Beträge ab 0, zum Beispiel 250 oder 1'000,50.",
  "ops.budget.note":
    "Beträge in USD, im Dashboard als CHF gezeigt (USD ≈ CHF). 0 schaltet ein Budget aus.",
  "ops.budget.save": "Budgets speichern",
  "ops.modes": "Modi",
  "ops.mode.col.state": "Status",
  "ops.mode.col.effective": "Alle Sessions",
  "ops.mode.col.test": "Testsitzungen",
  "ops.mode.col.baseline": "Terraform",
  "ops.mode.unreleased": "nicht freigegeben",
  "ops.mode.edit": "Ändern",
  "ops.mode.cancel": "Abbrechen",
  "ops.mode.enabled": "Modus an",
  "ops.mode.model": "Modell",
  "ops.mode.model.baseline": "Terraform ({model})",
  "ops.mode.depth": "Denktiefe",
  "ops.mode.depth.baseline": "Terraform ({depth})",
  "ops.mode.scope": "Modell und Denktiefe für",
  "ops.mode.scope.all": "alle Sessions",
  "ops.mode.scope.test": "nur Testsitzungen",
  "ops.mode.save": "Prüfen und speichern",
  "ops.mode.reset": "Zurück auf Terraform",
  "ops.modelDefault": "Modell-Standard ({depth})",
  "ops.log": "Änderungsprotokoll",
  "ops.log.empty": "Noch keine Änderung.",
  "ops.log.col.version": "Version",
  "ops.log.col.at": "Zeitpunkt",
  "ops.log.col.actor": "Wer",
  "ops.log.col.reason": "Grund",
  "ops.log.col.change": "Änderung",
  "ops.wipe": "Gefahrenzone: Reset für den Produktionsstart",
  "ops.wipe.counts":
    "Würde jetzt löschen: {sessions} Sessions, {turns} Turns, {events} Ereignisse, {aggregates} Wochen-Aggregate.",
  "ops.wipe.unavailable":
    "Nicht eingerichtet: Für den Reset fehlt der Passwort-Hash (CHATBOT_ADMIN_WIPE_PASSWORD_HASH).",
  "ops.wipe.confirm": "Zur Bestätigung die Projekt-ID «{text}» eintippen",
  "ops.wipe.password": "Reset-Passwort",
  "ops.wipe.submit": "Alle Gespräche und Statistiken endgültig löschen",
  "ops.wipe.busy": "Löscht…",
  "ops.wipe.done":
    "Gelöscht: {sessions} Sessions, {turns} Turns, {events} Ereignisse, {aggregates} Wochen-Aggregate. Stichtag {at}.",
  "ops.error.conflict":
    "Inzwischen hat jemand anderes gespeichert. Die Ansicht ist neu geladen: Änderung prüfen und nochmals speichern.",
  "ops.error.invalid": "Diese Einstellung kann nicht laufen: {problems}",
  "ops.error.probe":
    "Die Probe beim Anbieter ist gescheitert, nichts wurde gespeichert ({kind}): {detail}",
  "ops.error.unreadable":
    "Die Einstellungen sind gerade nicht lesbar (Firestore). Nichts wurde gespeichert.",
  "ops.error.password":
    "Falsches Reset-Passwort. Nach 5 Fehlversuchen ist der Reset 15 Minuten gesperrt.",
  "ops.error.confirm": "Die Projekt-ID stimmt nicht.",
  "ops.error.locked": "Zu viele falsche Passwörter: Der Reset ist 15 Minuten gesperrt.",
  "ops.error.wipeDisabled": "Der Reset ist nicht eingerichtet (kein Passwort-Hash).",
  "ops.error.wipeIncomplete":
    "Der Reset wurde unterbrochen, ein Teil ist schon gelöscht. Nochmals ausführen: Er setzt dort fort.",
  "ops.error.customer": "So lässt sich der Kunde nicht speichern: {problems}",
  "ops.error.unknownCustomer": "Diesen Kunden gibt es nicht mehr. Die Ansicht ist neu zu laden.",
  "ops.error.quietHours":
    "Die Ruhezeit braucht einen Beginn und ein Ende, und die beiden müssen sich unterscheiden.",
  "ops.probe.not_served": "Modell in der Region nicht verfügbar",
  "ops.probe.auth": "keine Berechtigung",
  "ops.probe.quota": "Kontingent erschöpft",
  "ops.probe.rejected": "Anfrage abgelehnt",
  "ops.probe.timeout": "Zeitüberschreitung",
  "ops.probe.unexpected": "unerwarteter Fehler",

  // --- Kunden & Domains ----------------------------------------------------
  "customers.saving": "Speichert…",
  "customers.list": "Kunden",
  "customers.empty": "Noch kein Kunde im Dashboard erfasst.",
  "customers.col.name": "Name",
  "customers.col.id": "ID im Partner-Report",
  "customers.col.origins": "Domains",
  "customers.edit": "Bearbeiten",
  "customers.remove": "Entfernen",
  "customers.remove.confirm":
    "{name} wirklich entfernen? Innert weniger Minuten lässt sich das Widget auf diesen Seiten nicht mehr öffnen.",
  "customers.remove.yes": "Ja, entfernen",
  "customers.remove.no": "Abbrechen",
  "customers.form.add": "Kunde erfassen",
  "customers.form.edit": "{name} bearbeiten",
  "customers.form.name": "Name",
  "customers.form.origins": "Domains, eine pro Zeile",
  "customers.form.originsHint":
    "https://www.partner.ch für genau diese Seite, https://*.partner.ch für alle Subdomains (die Hauptdomain selbst nicht). Ohne Pfad und ohne Port.",
  "customers.form.id": "ID im Partner-Report: {id}",
  "customers.form.idMissing": "Aus diesem Namen entsteht keine ID: Buchstaben oder Ziffern verwenden.",
  "customers.form.idTaken":
    "Die ID «{id}» ist schon vergeben. Den bestehenden Kunden bearbeiten oder einen anderen Namen wählen.",
  "customers.form.save": "Speichern",
  "customers.form.cancel": "Abbrechen",
  "customers.terraform": "Von Terraform festgelegt",
  "customers.terraform.origins": "Dürfen einbetten",
  "customers.terraform.partners": "Partner im Report",
  "customers.terraform.noPartners": "Keine Partner-Zuordnung konfiguriert.",

  // --- Analyse-Chat --------------------------------------------------------
  "analyst.title": "Analyse-Chat",
  "analyst.question": "Frage an den Analyse-Chat",
  "analyst.placeholder":
    "Frag nach Mustern, Lücken, Quellen oder Kosten im gewählten Zeitfenster… (Enter fragt, Shift+Enter neue Zeile)",
  "analyst.send": "Fragen",
  "analyst.new": "Neues Gespräch",
  "analyst.busy": "Analysiert das Datenpaket… das dauert bis zu einer Minute.",
  "analyst.basis":
    "Grundlage: {turns} Turns im Fenster {window}, {excerpts} Auszüge, Partner: {tenant} · Kosten {cost}",
  "analyst.allPartners": "alle",
  "analyst.refs": "Zitierte Gespräche:",
  "analyst.open": "Im Session Inspector öffnen",
  "analyst.error":
    "Die Analyse ist gescheitert ({kind}). Nochmals fragen; bei einer Zeitüberschreitung ein kürzeres Zeitfenster wählen.",
  "analyst.kind.empty": "keine Antwort erhalten",
  "analyst.starter.topics": "Worüber wird am meisten gefragt, und wo fehlt uns Inhalt?",
  "analyst.starter.sources":
    "Welche Quellen passen als bester Treffer nur schwach, und was würde sie verbessern?",
  "analyst.starter.negative": "Was fällt dir bei den negativ bewerteten Antworten auf?",
  "analyst.starter.cost": "Welcher Modus kostet am meisten pro Gespräch, und lohnt es sich?",
  "analyst.placeholderCheck":
    "Frag nach den Ergebnissen dieses Fragen-Checks… (Enter fragt, Shift+Enter neue Zeile)",
  "analyst.basisCheck": "Grundlage: der Fragen-Check, {excerpts} Auszüge · Kosten {cost}",
  "analyst.check.scope": "Datenpaket: der Fragen-Check «{name}» ({date}, Fragen: {n}).",
  "analyst.check.back": "zurück zu den Chatdaten",
  "analyst.starter.check.noPage":
    "Welche Fragen fanden keine passende Seite, und welche Seite fehlt dafür?",
  "analyst.starter.check.spread":
    "Bei welchen Gruppen verteilen sich die Fragen auf viele verschiedene beste Seiten?",
  "analyst.starter.check.weak":
    "Welche schwachen Treffer fallen auf, und was würde die Seite verbessern?",

  // --- Tagesbericht ----------------------------------------------------------
  "insights.title": "Tagesbericht",
  "insights.partial": "vorläufig",
  "insights.createdBy.scheduler": "Zeitplan",
  "insights.run.title": "Bericht erstellen",
  "insights.run.day": "Tag",
  "insights.run.submit": "Bericht erstellen",
  "insights.run.today":
    "Der heutige Tag läuft noch: Der Bericht wird vorläufig, ohne Vergleich mit den Tagen davor.",
  "insights.run.replaces": "Für den {day} gibt es schon einen Bericht: Der neue ersetzt ihn.",
  "insights.run.outOfRange": "Einen Tag vom {from} bis heute ({to}) wählen.",
  "insights.run.busy":
    "Erstellt den Bericht für den {day}; das dauert bis zu etwa drei Minuten. Solange lässt sich kein zweiter starten.",
  "insights.run.elapsed": "Läuft seit {elapsed}.",
  "insights.list.title": "Gespeicherte Berichte",
  "insights.list.none":
    "Noch kein Bericht gespeichert. Der erste entsteht nachts um 03:00 Uhr oder mit «Bericht erstellen».",
  "insights.list.col.day": "Tag",
  "insights.list.col.conversations": "Gespräche",
  "insights.list.col.questions": "Fragen",
  "insights.list.col.narrative": "Text",
  "insights.list.col.created": "Erstellt",
  "insights.list.withText": "mit Text",
  "insights.list.figuresOnly": "nur Zahlen",
  "insights.list.created": "{when} · {by}",
  "insights.report.title": "Bericht vom {day}",
  "insights.report.meta": "Erstellt am {when} von {by}",
  "insights.report.compare": "Die Trends vergleichen mit dem Schnitt der {days} Tage davor.",
  "insights.report.noCompare":
    "Vorläufig: Erstellt, während der Tag lief, darum ohne Vergleich mit den Tagen davor.",
  "insights.report.incomplete":
    "Mehr Daten als die Lesegrenze: Die Zahlen dieses Tages sind unvollständig.",
  "insights.narrative.title": "Bericht",
  "insights.narrative.meta": "Geschrieben von {model} · Kosten {cost}",
  "insights.failure.no_conversations": "Kein Text: An diesem Tag gab es keine Gespräche.",
  "insights.failure.empty":
    "Kein Text: Das Modell lieferte eine leere Antwort. Ein neuer Lauf versucht es nochmals.",
  "insights.failure.timeout":
    "Kein Text: Das Modell hat nicht rechtzeitig geantwortet. Ein neuer Lauf versucht es nochmals.",
  "insights.failure.quota":
    "Kein Text: Das Kontingent beim Modellanbieter war erschöpft. Später nochmals erstellen.",
  "insights.failure.auth":
    "Kein Text: Die Berechtigung beim Modellanbieter fehlte. Die Konfiguration des Backends prüfen.",
  "insights.failure.not_served":
    "Kein Text: Das Modell ist in der Region nicht verfügbar. Das Modell des Analyse-Chats prüfen.",
  "insights.failure.rejected":
    "Kein Text: Der Modellanbieter hat die Anfrage abgelehnt. Das Modell des Analyse-Chats prüfen.",
  "insights.failure.unexpected":
    "Kein Text: Beim Schreiben trat ein unerwarteter Fehler auf. Ein neuer Lauf versucht es nochmals.",
  "insights.failure.other": "Kein Text ({kind}).",
  "insights.day.title": "Der Tag in Zahlen",
  "insights.totals.conversations": "Gespräche",
  "insights.totals.questions": "Fragen",
  "insights.totals.noAnswer": "«Keine Antwort»-Ereignisse",
  "insights.modes.title": "Je Modus",
  "insights.col.mode": "Modus",
  "insights.col.conversations": "Gespräche",
  "insights.col.questions": "Fragen",
  "insights.col.replies": "Antworten auf Rückfragen",
  "insights.col.ratings": "Bewertungen",
  "insights.col.oneQuestion": "Ein-Frage-Gespräche",
  "insights.col.noPage": "Ohne passende Seite",
  "insights.col.weakMatch": "Schwacher Treffer",
  "insights.terms.title": "Gefragte Begriffe",
  "insights.terms.baseline": "Ø/Tag der {days} Tage davor",
  "insights.terms.none": "Keine Begriffe an diesem Tag.",
  "insights.modeDetail.title": "Was gefragt wurde, je Modus",
  "insights.unit.questions": "Fragen",
  "insights.unit.conversations": "Gespräche",
  "insights.unit.messages": "Nachrichten",
  "insights.none": "keine",
  "insights.error.invalid_day":
    "Dieser Tag lässt sich nicht erstellen: nur heute oder einer der {days} Tage davor.",
  "insights.error.storage_failed":
    "Der Bericht wurde erstellt, liess sich aber nicht speichern (Firestore). Nochmals erstellen, am besten in ein paar Minuten.",
  "insights.error.not_found":
    "Diesen Bericht gibt es nicht (mehr). «Bericht erstellen» für diesen Tag erstellt ihn neu.",
  "insights.error.network": "Keine Verbindung zum Backend. Nochmals versuchen.",

  // --- Fragen-Check --------------------------------------------------------
  "qc.unnamed": "(ohne Namen)",
  "qc.paste.title": "Liste einfügen",
  "qc.paste.name": "Name (freiwillig)",
  "qc.paste.text": "Suchanfragen: aus Excel kopiert oder eine Frage pro Zeile",
  "qc.paste.placeholder":
    "Den Export «Suchanfragen» der Search Console in Excel öffnen, die Tabelle kopieren und hier einfügen, oder eine Frage pro Zeile schreiben.",
  "qc.paste.group": "Gruppieren",
  "qc.paste.busy":
    "Gruppiert… Was die Regeln nicht einordnen, ordnet ein Modell ein; das dauert meist unter einer Minute, bei sehr vielen offenen Zeilen bis zu drei Minuten. Die Ansicht bleibt bedienbar.",
  "qc.paste.tooLong": "Zu lang: höchstens {max} Zeichen pro Liste.",
  "qc.groups.title": "Gruppen",
  "qc.groups.listMeta": "«{name}» vom {date} · Fragen: {rows} · Gruppen: {groups}",
  "qc.groups.stats":
    "Nach Regeln eingeordnet: {rules} · vom Modell: {model} · nicht zugeordnet: {unplaced} · doppelte zusammengelegt: {duplicates}",
  "qc.groups.modelFailed":
    "Das Modell war nicht erreichbar: Was die Regeln nicht einordnen konnten, steht unter «Nicht zugeordnet».",
  "qc.groups.close": "Liste schliessen",
  "qc.groups.mode": "Gruppen bilden",
  "qc.groups.byKeyword": "nach Stichwort",
  "qc.groups.byType": "nach Frageart",
  "qc.groups.col.select": "Auswahl",
  "qc.groups.col.group": "Gruppe",
  "qc.groups.col.kind": "Art",
  "qc.groups.col.questions": "Fragen",
  "qc.groups.col.clicks": "Klicks",
  "qc.groups.col.impressions": "Impressionen",
  "qc.groups.col.ctr": "CTR",
  "qc.groups.col.position": "Ø Position",
  "qc.groups.selectGroup": "Gruppe «{label}» ankreuzen",
  "qc.groups.selectQuestion": "Frage «{query}» ankreuzen",
  "qc.kind.beruf": "Beruf",
  "qc.kind.begriff": "Begriff",
  "qc.kind.thema": "Thema",
  "qc.kind.kein_bildungsthema": "Kein Bildungsthema",
  "qc.kind.unvollstaendig": "Unvollständig",
  "qc.kind.nicht_zugeordnet": "Nicht zugeordnet",
  "qc.topic.berufswahl": "Berufswahl",
  "qc.topic.bewerbung": "Bewerbung",
  "qc.topic.lehrstellensuche": "Lehrstellensuche",
  "qc.topic.ueberblick": "Überblick",
  "qc.topic.zukunft": "Zukunft",
  "qc.topic.lohn": "Lohn",
  "qc.topic.dauer": "Dauer",
  "qc.topic.nach_der_lehre": "Nach der Lehre",
  "qc.qtype.was_ist": "Was ist …?",
  "qc.qtype.berufsbild": "Berufsbild",
  "qc.qtype.alltag": "Berufsalltag",
  "qc.qtype.lohn": "Lohn",
  "qc.qtype.dauer": "Dauer",
  "qc.qtype.voraussetzungen": "Voraussetzungen",
  "qc.qtype.berufswahl": "Berufswahl",
  "qc.qtype.weiterbildung": "Weiterbildung",
  "qc.qtype.bewerbung": "Bewerbung",
  "qc.qtype.lehrstelle": "Lehrstelle",
  "qc.qtype.ort": "Ort",
  "qc.qtype.vergleich": "Vergleich",
  "qc.qtype.anmeldung": "Anmeldung",
  "qc.qtype.stichwort": "Nur ein Stichwort",
  "qc.qtype.sonstige": "Sonstige",
  "qc.verdict.error": "Fehler",
  "qc.verdict.no_page": "Keine passende Seite",
  "qc.verdict.support": "An den Kundendienst verwiesen",
  "qc.verdict.weak": "Schwacher Treffer",
  "qc.verdict.uncited": "Ohne Quellenangabe",
  "qc.verdict.answered": "Beantwortet",
  "qc.flag.listings": "Lehrstellen-Antwort",
  "qc.bar.label": "Auswahl",
  "qc.bar.none": "Gruppen oder Fragen ankreuzen, um sie zu prüfen.",
  "qc.bar.countOne": "1 Frage",
  "qc.bar.count": "{n} Fragen",
  "qc.bar.cost": "≈ {cost}",
  "qc.bar.duration": "ca. {d}",
  "qc.bar.estimating": "Schätzt Kosten und Dauer…",
  "qc.bar.estimateFailed": "Keine Schätzung: {error}",
  "qc.bar.estimateRetry": "Nochmals schätzen",
  "qc.bar.basis":
    "Geschätzt aus den letzten {turns} ersten FAQ-Antworten ({model}), dazu {search} Suche je Frage.",
  "qc.bar.basisAssumed":
    "Eine Annahme: Es gibt noch keine 10 ersten FAQ-Antworten zum Messen ({model}). Dazu {search} Suche je Frage.",
  "qc.bar.note":
    "Die Fragen laufen als Testsitzungen: In der Statistik erscheinen sie nur mit «Testsitzungen einbeziehen», ins Tagesbudget und ins Limit je IP zählen sie nicht.",
  "qc.bar.tooMany": "Höchstens {max} Fragen pro Prüfung: {over} abwählen.",
  "qc.bar.page": "Von welcher Seite aus",
  "qc.bar.pageOption": "{origin} · {sessions} Sessions",
  "qc.bar.pageHint":
    "Die Fragen laufen, als kämen sie von dieser einbettenden Seite, mit deren Quellen und Feeds. Die Seiten mit den meisten Sessions zuerst.",
  "qc.bar.check": "Prüfen",
  "qc.bar.starting": "Startet…",
  "qc.bar.busy":
    "Eine Prüfung läuft: Eine neue lässt sich starten, sobald sie pausiert oder fertig ist.",
  "qc.run.title": "Prüfung",
  "qc.run.meta": "«{name}» vom {date} · von {by} · Seite: {page}",
  "qc.run.noPage": "keine",
  "qc.run.progress": "{done} von {total} geprüft, {failed} fehlgeschlagen",
  "qc.run.cost": "Kosten bisher: {cost}",
  "qc.run.pause": "Pausieren",
  "qc.run.resume": "Weiter",
  "qc.run.close": "Prüfung schliessen",
  "qc.run.running":
    "Läuft. Andere Ansichten lassen sich öffnen: Die Prüfung geht weiter, solange dieses Browserfenster offen ist.",
  "qc.run.pausing": "Hält nach dem laufenden Schritt an…",
  "qc.run.paused": "Pausiert. «Weiter» setzt dort fort.",
  "qc.run.done": "Fertig: Alle Fragen sind geprüft.",
  "qc.stop.chat_paused":
    "Angehalten: Der Chat ist pausiert (Schalter, Ruhezeit oder Tagesbudget unter Betrieb › Schalter & Budgets). «Weiter», sobald er wieder läuft.",
  "qc.stop.mode_disabled":
    "Angehalten: Der FAQ-Modus ist ausgeschaltet (Betrieb › Schalter & Budgets). «Weiter», sobald er wieder an ist.",
  "qc.stop.storage_failed":
    "Pausiert: Das Backend konnte ein Ergebnis nicht speichern; die übrigen Ergebnisse dieses Schritts sind gespeichert. «Weiter» setzt die Prüfung fort, am besten in ein paar Minuten.",
  "qc.stop.network":
    "Pausiert: Das Backend hat auch nach mehreren Versuchen nicht geantwortet. «Weiter» versucht es nochmals.",
  "qc.stop.not_found": "Diese Prüfung gibt es nicht mehr.",
  "qc.stop.failed": "Angehalten ({error}). «Weiter» versucht es nochmals.",
  "qc.stop.stalled":
    "Pausiert: Seit {minutes} Minuten ist keine Frage fertig geworden, obwohl das Backend antwortet. «Weiter» versucht es nochmals.",
  "qc.error.chat_paused":
    "Der Chat ist pausiert (Betrieb › Schalter & Budgets): Eine Prüfung startet erst, wenn er wieder läuft.",
  "qc.error.mode_disabled":
    "Der FAQ-Modus ist ausgeschaltet (Betrieb › Schalter & Budgets): Eine Prüfung startet erst, wenn er wieder an ist.",
  "qc.error.invalid_rows":
    "Diese Auswahl lässt sich nicht prüfen: 1 bis {max} Fragen dieser Liste ankreuzen.",
  "qc.error.invalid_page":
    "Von dieser Seite aus lässt sich nicht prüfen: Das Backend führt sie nicht als einbettende Seite. Eine andere Seite wählen.",
  "qc.error.not_found": "Diese Liste oder Prüfung gibt es nicht mehr.",
  "qc.error.empty":
    "In der Liste steht keine Frage: die Tabelle aus Excel einfügen oder eine Frage pro Zeile.",
  "qc.error.too_many_rows":
    "Zu viele Zeilen: höchstens {max} pro Liste. Die Liste teilen und die Teile nacheinander einfügen.",
  "qc.error.network": "Keine Verbindung zum Backend. Nochmals versuchen.",
  "qc.results.title": "Ergebnisse",
  "qc.results.none": "Noch keine Ergebnisse: Die ersten erscheinen nach dem ersten Schritt.",
  "qc.results.byGroup": "Je Gruppe",
  "qc.results.byQuestion": "Je Frage, die Probleme zuerst",
  "qc.results.col.group": "Gruppe",
  "qc.results.col.questions": "Fragen",
  "qc.results.col.pages": "Verschiedene beste Seiten",
  "qc.results.col.question": "Frage",
  "qc.results.col.verdict": "Ergebnis",
  "qc.results.col.bestPage": "Beste Seite",
  "qc.results.col.answer": "Antwort",
  "qc.results.col.cost": "Kosten",
  "qc.results.col.session": "Session",
  "qc.results.score": "Score {score}",
  "qc.results.noAnswer": "keine Antwort gespeichert",
  // Why a question failed (CheckResult.error); any other code shows as it is.
  "qc.resultError.TimeoutError":
    "Zeitüberschreitung: Die Frage bekam innerhalb ihres Zeitbudgets keine Antwort.",
  "qc.resultError.no_turn": "Kein Turn: Der Chat beendete die Antwort, ohne einen Turn zu speichern.",
  "qc.resultError.turn_not_found": "Der Turn der Antwort war nicht mehr auffindbar.",
  "qc.resultError.test_session_unmarked":
    "Nicht gestellt: Die Session liess sich nicht als Testsitzung markieren.",
  "qc.resultError.model_refusal": "Der Sicherheitsfilter des Modellanbieters hat die Antwort abgelehnt.",
  "qc.resultError.http": "Der Chat antwortete mit HTTP {status}.",
  "qc.resultError.timeout": "Zeitüberschreitung beim Modell: Die Antwort blieb aus.",
  "qc.resultError.llm_error": "Das Modell antwortete mit einem Fehler.",
  "qc.resultError.service_unavailable":
    "Der Chat war kurz nicht verfügbar (Speicher oder Infrastruktur).",
  "qc.resultError.chat_paused": "Der Chat war pausiert.",
  "qc.resultError.mode_disabled": "Der FAQ-Modus war ausgeschaltet.",
  "qc.resultError.unknown": "Der Chat meldete einen Fehler ohne Code.",
  "qc.results.inspector": "Im Session Inspector",
  "qc.results.export": "⬇ CSV",
  "qc.results.exportTitle": "Die Ergebnisse als CSV für Excel herunterladen",
  "qc.results.discuss": "Im Analyse-Chat besprechen",
  "qc.history.title": "Frühere Listen und Prüfungen",
  "qc.history.lists": "Frühere Listen",
  "qc.history.checks": "Frühere Prüfungen",
  "qc.history.noLists": "Noch keine Liste gespeichert.",
  "qc.history.noChecks": "Noch keine Prüfung.",
  "qc.history.col.name": "Name",
  "qc.history.col.created": "Erstellt",
  "qc.history.col.by": "Von",
  "qc.history.col.rows": "Fragen",
  "qc.history.col.groups": "Gruppen",
  "qc.history.col.page": "Seite",
  "qc.history.col.progress": "Stand",
  "qc.history.col.cost": "Kosten",
  "qc.history.status.running": "nicht fertig",
  "qc.history.status.done": "fertig",
  "qc.history.open": "Öffnen",
  "qc.history.delete": "Löschen",
  "qc.history.confirmList": "Die Liste «{name}» endgültig löschen?",
  "qc.history.confirmCheck":
    "Die Prüfung «{name}» vom {date} mit ihren Ergebnissen endgültig löschen?",
  "qc.history.yes": "Ja, löschen",
  "qc.history.no": "Abbrechen",
  "qc.history.busy": "Eine Prüfung läuft: Pausieren, dann lässt sich eine andere öffnen.",
  "qc.export.meta.check": "Prüfung",
  "qc.export.meta.created": "Erstellt",
  "qc.export.meta.by": "Von",
  "qc.export.meta.page": "Seite",
  "qc.export.meta.progress": "Stand",
  "qc.export.meta.cost": "Kosten USD",
  "qc.export.meta.basis": "Grundlage",
  "qc.export.meta.basisNote":
    "Testsitzungen durch die FAQ: in der Statistik nur mit «Testsitzungen einbeziehen», nicht im Tagesbudget und nicht im Limit je IP. Kosten in USD, wie im Dashboard bepreist.",
  "qc.export.col.group": "Gruppe",
  "qc.export.col.kind": "Art",
  "qc.export.col.qtype": "Frageart",
  "qc.export.col.clicks": "Klicks",
  "qc.export.col.impressions": "Impressionen",
  "qc.export.col.verdict": "Ergebnis",
  "qc.export.col.listings": "Lehrstellen-Antwort",
  "qc.export.col.bestUrl": "Beste Seite",
  "qc.export.col.bestTitle": "Titel der besten Seite",
  "qc.export.col.score": "Score",
  "qc.export.col.costUsd": "Kosten USD",
  "qc.export.col.error": "Fehler",

  // --- Testseite -----------------------------------------------------------
  "testpage.embed": "Einbettung",
  "testpage.presets": "Vorlagen:",
  "testpage.preset.gateway": "gateway.one: Sprechblase mit Modus-Menü",
  "testpage.preset.profession": "Berufsseite: Beruf im Inhalt",
  "testpage.preset.partner": "Partner: eigene Farbe und Marke",
  "testpage.preset.search": "Startseite: Suchleiste",
  "testpage.variant": "Art",
  "testpage.variant.bubble": "Sprechblase (loader.js)",
  "testpage.variant.inline": "Im Inhalt (embed.js)",
  "testpage.variant.search": "Suchleiste (search.js)",
  "testpage.mode": "Modus",
  "testpage.profession": "Beruf",
  "testpage.profession.none": "Die Person wählt im Chat",
  "testpage.profession.id": "Beruf-ID",
  "testpage.profession.name": "Beruf-Name",
  "testpage.menu": "Modi im Menü",
  "testpage.lang": "Sprache",
  "testpage.theme": "Darstellung",
  "testpage.theme.auto": "wie das Gerät",
  "testpage.theme.light": "hell",
  "testpage.theme.dark": "dunkel",
  "testpage.position": "Position",
  "testpage.position.right": "unten rechts",
  "testpage.position.left": "unten links",
  "testpage.color": "Hauptfarbe",
  "testpage.brand": "Markenname",
  "testpage.logo": "Logo-URL (https oder data:image, höchstens 4 KB)",
  "testpage.greeting": "Begrüssung",
  "testpage.starters": "Startfragen, eine pro Zeile",
  "testpage.q": "Erste Frage, die der Chat selbst stellt",
  "testpage.examples": "Beispielfragen der Suchleiste, eine pro Zeile",
  "testpage.placeholder": "Platzhalter der Suchleiste",
  "testpage.preview": "Vorschau",
  "testpage.preview.desktop": "Desktop",
  "testpage.preview.mobile": "Handy",
  "testpage.preview.reload": "Neu laden",
  "testpage.preview.frame": "Vorschau der Einbettung",
  "testpage.preview.title": "Beispielseite",
  "testpage.preview.intro": "So erscheint das Widget auf einer Seite, die es einbettet.",
  "testpage.token.on": "Sessions aus dieser Vorschau sind Testsitzungen (Token bis {until}).",
  "testpage.token.off":
    "Kein Test-Schlüssel gesetzt (CHATBOT_TEST_SESSION_KEYWORD): Sessions aus dieser Vorschau zählen wie echte, in Statistik und Kosten.",
  "testpage.token.error":
    "Kein Test-Token ({error}): Sessions aus dieser Vorschau zählen wie echte, in Statistik und Kosten.",
  "testpage.code": "Code zum Kopieren",
  "testpage.copy": "Code kopieren",
  "testpage.copied": "Kopiert.",
  "testpage.copyFailed": "Kopieren ging nicht: den Code markieren und von Hand kopieren.",
  "testpage.model": "So antwortet dieser Modus",
  "testpage.model.test": "In dieser Vorschau (Testsitzungen): {run}",
  "testpage.model.all": "Für alle anderen Sessions: {run}",
  "testpage.model.switch": "Modelle unter Schalter & Budgets",
  "testpage.guide.title": "So funktioniert die Suchleiste",
  "testpage.guide.what":
    "search.js setzt ein Fragefeld auf die Seite, dort, wo Besucher sonst suchen würden. Wer eine Frage tippt und «Fragen» drückt, bekommt die Antwort des Chats in einem Fenster über der Seite, ohne sie zu verlassen. So steht der Chat dort, wo Fragen entstehen, und braucht auf der Seite nur den Platz des Feldes.",
  "testpage.guide.flow": "Was Besucher erleben",
  "testpage.guide.flow.examples":
    "Solange niemand tippt, schreibt das Feld Beispielfragen vor. Beim Hineinklicken hält es an; ist am Gerät «weniger Bewegung» eingestellt, bleibt es still.",
  "testpage.guide.flow.dialog":
    "«Fragen» öffnet ein Fenster mit dem Chat im gewählten Modus. Wer den Datenschutz-Hinweis noch nicht bestätigt hat, sieht zuerst ihn; danach geht die Frage als erste Nachricht ab.",
  "testpage.guide.flow.fresh":
    "Jede Suche beginnt ein neues Gespräch. Esc oder «Schliessen» beendet es, ⤢ zeigt den Chat bildschirmfüllend. Solange das Fenster offen ist, ist die übrige Seite gesperrt, damit Tastatur und Screenreader im Chat bleiben.",
  "testpage.guide.flow.private":
    "Die Frage steht im Fragment der Adresse (nach «#»), das der Browser nie mitschickt, und landet so in keinem Server-Log. Das Google Analytics der Seite bekommt nur Ereignisnamen mit dem Modus (etwa chatbot_search_opened), nie einen Text.",
  "testpage.guide.how": "So kommt sie auf eine Seite",
  "testpage.guide.how.allow":
    "Die Domain der Seite freigeben, unter Kunden & Domains oder in Terraform. Sonst zeigt der Browser den Chat im Fenster nicht.",
  "testpage.guide.how.paste":
    "Den Code zum Kopieren (unten) an die Stelle der Seite setzen, wo das Feld stehen soll. Das <div> bestimmt Platz und Breite, «data-target» im Script nennt es. Ein <div> nimmt das Feld immer an, Listen- und Tabellenelemente nicht.",
  "testpage.guide.how.options":
    "Alles Weitere über die data-Attribute des Scripts, oben einstellbar: «data-mode» (Modus), «data-lang» (Sprache von Feld und Antworten), «data-examples» (bis zu 10 Beispielfragen als JSON-Liste), «data-placeholder», «data-primary-color», «data-brand-name». Auf dem Blau von gateway.one ist der Knopf orange; eine Partnerfarbe wird die eigene Akzentfarbe.",
  "testpage.guide.how.test":
    "In der Vorschau ausprobieren: Ihre Sessions sind Testsitzungen, auf der Partnerseite entstehen echte. Ist der Chat pausiert, bleibt das Feld stehen, und der Chat im Fenster zeigt die Pause selbst an.",

  // --- Glossary ------------------------------------------------------------
  "glossary.intro":
    "Die Begriffe des Dashboards: was jeder bedeutet und warum er zählt. Dieselben Begriffe stehen als ⓘ und als Erklärzeile direkt an den Panels.",
  "glossary.term": "Begriff",
  "glossary.what": "Was es ist",
  "glossary.why": "Warum es zählt",
} as const;
